import { useState } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';

type CtaTarget = 'tg' | 'off';

function useCtaTarget(): CtaTarget {
  const [searchParams] = useSearchParams();
  const cta = searchParams.get('cta');
  if (cta === 'off') return 'off';
  return 'tg';
}

// Ведём на наш сайт, а не в Telegram: у части людей он не работает, и такой
// переход для них — тупик. На сайте курс открывается сразу, без установки чего-либо.
const COURSES_SITE = 'https://kurs.mugstel.ru';

function getCtaUrl(collectionId?: string): string {
  return collectionId
    ? `${COURSES_SITE}/collections/${collectionId}`
    : `${COURSES_SITE}/`;
}

interface EmbedLayoutProps {
  children?: React.ReactNode;
}

export default function EmbedLayout({ children }: EmbedLayoutProps) {
  const { id } = useParams<{ id: string }>();
  const ctaTarget = useCtaTarget();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 pb-14">
        {children || <Outlet />}
      </div>
      {ctaTarget !== 'off' && (
        <EmbedFooter collectionId={id} />
      )}
    </div>
  );
}

function EmbedFooter({ collectionId }: { collectionId?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const url = getCtaUrl(collectionId);

  const handleOpenCourse = () => {
    window.open(url, '_blank');
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      {/* Основная плашка */}
      <div 
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">🎓</span>
          <span className="text-sm text-gray-700">
            Весь курс целиком — <span className="text-blue-600">на нашем сайте</span>
          </span>
          <span className={`text-gray-400 text-xs transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenCourse();
          }}
          className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Перейти
        </button>
      </div>

      {/* Раскрывающиеся преимущества */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 bg-gray-50">
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Прогресс сохраняется</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Сертификат после прохождения</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>другие курсы по теме</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>без установки приложений</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmbedPaywall({ collectionId, collectionName }: { collectionId: string; collectionName?: string }) {
  const ctaTarget = useCtaTarget();

  if (ctaTarget === 'off') {
    return (
      <div className="mx-4 mb-4 bg-gray-100 rounded-2xl p-5 text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Контент заблокирован</h3>
        <p className="text-gray-500 text-sm">Для продолжения обратитесь к владельцу курса</p>
      </div>
    );
  }

  const url = getCtaUrl(collectionId);

  return (
    <div className="mx-4 mb-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-5 text-white text-center">
      <div className="text-4xl mb-3">🔒</div>
      <h3 className="text-lg font-bold mb-1">Продолжить обучение</h3>
      <p className="text-white/80 text-sm mb-4">
        Полный курс{collectionName ? ` «${collectionName}»` : ''} открыт на нашем сайте — бесплатно, с сертификатом.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-gray-100 transition-all"
      >
        Открыть курс
      </a>
    </div>
  );
}